/**
 * Battement de cœur de la session : détecte une révocation côté SSO pendant
 * qu'on travaille, et prévient avant de déconnecter.
 *
 * Sans lui, un compte désactivé ou un SSO redémarré ne se voit qu'au prochain
 * appel d'API, sous la forme d'une erreur sèche au milieu d'une saisie — voire
 * d'une erreur SQL, la session vidée laissant passer un identifiant
 * inexploitable. Ici l'éditeur est averti, voit le temps qu'il lui reste, et
 * peut mettre son travail à l'abri.
 *
 * La route `/api/auth/check` répond en trois états plutôt qu'en code HTTP :
 * seul `revoked` déclenche le compte à rebours. Une panne du SSO
 * (`unreachable`) ne déconnecte personne — c'est toute la raison d'être de
 * cette route, `/api/auth/session` répondant 401 dans les deux cas.
 *
 * Même mécanisme que dans les applications sœurs du workspace (Adhérence,
 * Speecraft, Comparateur).
 */

const verifierSession = $fetch as unknown as (
  url: string,
) => Promise<{ status: 'ok' | 'revoked' | 'unreachable' | 'sans_acces' }>

const HEARTBEAT_INTERVAL = 2 * 60 * 1000
const COUNTDOWN_SECONDS = 60
const INITIAL_DELAY = 10_000

/**
 * @param activityLock Passé à `true` pendant une action qu'on ne veut pas
 *                     interrompre — un enregistrement en cours. L'avertissement
 *                     attend qu'elle s'achève.
 */
export function useAuthHeartbeat(activityLock?: Ref<boolean>) {
  const revoked = ref(false)
  const countdown = ref(COUNTDOWN_SECONDS)
  const deferred = ref(false)
  const authStore = useAuthStore()

  let intervalId: ReturnType<typeof setInterval> | null = null
  let countdownId: ReturnType<typeof setInterval> | null = null
  let initialTimerId: ReturnType<typeof setTimeout> | null = null

  async function check() {
    if (revoked.value) return
    if (authStore.loggingOut || !authStore.authenticated) return

    try {
      const res = await verifierSession('/api/auth/check')

      /*
       * Accès retiré en cours de session (#190) : la page de blocage, tout de
       * suite et sans compte à rebours — il n'y a rien à sauver, chaque
       * enregistrement serait refusé.
       */
      if (res.status === 'sans_acces') {
        if (authStore.user) authStore.user.sansAcces = true
        await navigateTo('/acces-refuse')
        return
      }

      if (res.status !== 'revoked') return

      // Une déconnexion volontaire a pu démarrer pendant l'appel : elle a la
      // priorité, inutile d'avertir quelqu'un qui s'en va déjà.
      if (authStore.loggingOut) return

      revoked.value = true

      if (activityLock?.value) {
        deferred.value = true
      }
      else {
        startCountdown()
      }
    }
    catch {
      // Coupure réseau locale : on retentera au prochain battement. Rien ici ne
      // doit déconnecter, faute de quoi une micro-coupure suffirait à éjecter
      // un éditeur parfaitement légitime.
    }
  }

  if (activityLock) {
    watch(activityLock, (locked) => {
      if (!locked && deferred.value) {
        deferred.value = false
        startCountdown()
      }
    })
  }

  function startCountdown() {
    countdown.value = COUNTDOWN_SECONDS

    // Le battement s'arrête : la session est déjà perdue, continuer à
    // l'interroger n'apprendrait plus rien.
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }

    countdownId = setInterval(() => {
      countdown.value--
      if (countdown.value <= 0) forceLogout()
    }, 1000)
  }

  function forceLogout() {
    stop()
    authStore.logout()
  }

  function start() {
    if (!import.meta.client) return
    if (!authStore.authenticated) return

    // Premier contrôle après dix secondes : laisser l'application se charger
    // avant d'ajouter une requête au démarrage.
    initialTimerId = setTimeout(check, INITIAL_DELAY)
    intervalId = setInterval(check, HEARTBEAT_INTERVAL)
  }

  function stop() {
    if (initialTimerId) { clearTimeout(initialTimerId); initialTimerId = null }
    if (intervalId) { clearInterval(intervalId); intervalId = null }
    if (countdownId) { clearInterval(countdownId); countdownId = null }
  }

  /*
   * Une déconnexion volontaire, ou une session perdue par un autre chemin,
   * arrête le battement : sans cela il continuerait d'interroger le serveur
   * pour une session qui n'existe plus.
   */
  watch(() => authStore.authenticated, (isAuth) => {
    if (!isAuth) stop()
  })

  onMounted(start)
  onUnmounted(stop)

  return {
    revoked: readonly(revoked),
    countdown: readonly(countdown),
    deferred: readonly(deferred),
    forceLogout,
  }
}
